import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Dialog, DialogContent, DialogTitle, Grid } from "@mui/material";
import CustomInput from "../CommonComp/CustomInput/CustomInput";
import CustomButton from "../CommonComp/Buttons/CustomButton";
import { useDispatch } from "react-redux";
import { addVoucherGiftCard } from "../../Services/OrderApis";
import { action, logsMessagesList, modulesNames } from '../../data/logsMessages';
import { adminAddLogs } from '../../Services/commonService';

function AddVoucherModal({ open, handleOpen,
  handleConfirmVoucher, order_data, type }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleConfirm = async (values, { resetForm }) => {
    setLoading(true);

    const voucher_flag = type === "update" ? "update" : "add";
    const payload = {
      voucher_expiry: values.expiry_date,
      voucher_price: values.voucher_price,
      order_id: order_data?.order_id,
      voucher_flag,
      voucher_email: values.voucher_email,
    };

    try {
      const resp = await addVoucherGiftCard(payload);
      if (resp) {
        const payloadForLogs = {
                  action: voucher_flag === "add" ? action?.add : action?.update,
                  module: modulesNames?.orders,
                  description: voucher_flag === "add"
                  ? logsMessagesList?.order?.voucherAdded
                  : logsMessagesList?.order?.voucherUpdate,
                };
                await adminAddLogs(payloadForLogs);
        handleConfirmVoucher();
        handleOpen(false);
      } else {
        console.error("API Error:", resp.message || "Failed to add voucher");
      }
    } catch (error) {
      console.error("Error adding voucher:", error);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      expiry_date: order_data?.voucher_expiry || "",
      voucher_price: order_data?.voucher_price || "",
      voucher_email: order_data?.voucher_email || "",
    },
    validationSchema: Yup.object({
      expiry_date: Yup.date()
        .required('Expiry Date is required.'),
      voucher_price: Yup.number()
        .required('Voucher Price is required.')
        .positive('Voucher Price must be a positive number.')
        .typeError('Voucher Price must be a number.'),
      voucher_email: Yup.string()
        .email('Invalid email format')
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email must be valid and not contain numbers or special characters in the middle')
        .required('Email is required.'),
    }),
    onSubmit: handleConfirm,
  });

  useEffect(() => {
    if (order_data) {
      formik.setValues({
        expiry_date: order_data.voucher_expiry || "",
        voucher_price: order_data.voucher_price || "",
        voucher_email: order_data.voucher_email || "",
      });
    }
  }, [order_data]);

  return (
    <div>
      <Dialog
        maxWidth={"sm"}
        open={open}
        fullWidth
        onClose={() => handleOpen(false)}
      >
        <DialogTitle variant="h3" padding={3}>
          {type === "update" ? "Update" : "Add"} Voucher
        </DialogTitle>
        <DialogContent>
          <Box noValidate component="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <CustomInput
                  variant="outlined"
                  fullWidth
                  name="voucher_email"
                  label="Email"
                  placeholder="Email"
                  type="email"
                  value={formik.values.voucher_email}
                  onChange={formik.handleChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    classes: {
                      notchedOutline: "rounded",
                    },
                  }}
                  error={
                    formik.touched.voucher_email &&
                    Boolean(formik.errors.voucher_email)
                  }
                  helperText={
                    formik.touched.voucher_email && formik.errors.voucher_email
                  }
                />
              </Grid>
              <Grid item xs={12} md={6 }>
                <CustomInput
                  variant="outlined"
                  fullWidth
                  name="expiry_date"
                  label=" Expiry Date"
                  type="date"
                  value={formik.values.expiry_date}
                  onChange={formik.handleChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    classes: {
                      notchedOutline: "rounded",
                    },
                  }}
                  error={
                    formik.touched.expiry_date &&
                    Boolean(formik.errors.expiry_date)
                  }
                  helperText={
                    formik.touched.expiry_date && formik.errors.expiry_date
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomInput
                  variant="outlined"
                  fullWidth
                  name="voucher_price"
                  label="Voucher Price"
                  placeholder="Voucher Price"
                  type="number"
                  value={formik.values.voucher_price}
                  onChange={formik.handleChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    classes: {
                      notchedOutline: "rounded",
                    },
                  }}
                  error={
                    formik.touched.voucher_price &&
                    Boolean(formik.errors.voucher_price)
                  }
                  helperText={
                    formik.touched.voucher_price && formik.errors.voucher_price
                  }
                />
              </Grid>
            </Grid>
            <div
              style={{ display: "flex", justifyContent: "end", marginTop: 15 }}
            >
              <CustomButton
                color={"buttonPrimary"}
                type="submit"
                loading={loading}
              >
                {type === "update" ? "Update" : "Add"}
              </CustomButton>
              <CustomButton
                onClick={() => {
                  formik.resetForm();
                  handleOpen(false);
                }}
                disabled={loading}
              >
                Cancel
              </CustomButton>
            </div>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AddVoucherModal;
